import {cloneDeep} from 'lodash';
import {ProjectType} from '@contentlab/api/project/types';
import type {
  DraftAiAvatarProject,
  DraftCaptionsProject,
  DraftDubbingProject,
  DraftReformatProject,
  DraftRephrasingProject,
  UploadedVideo,
} from '@contentlab/stores/contentLabStore';
import {AiAvatarEditorTab} from '@contentlab/stores/contentLabStore';
import {AvatarKind, ConfigMode} from '@contentlab/types/aiAvatars';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import {FeatureName, Project} from '@contentlab/types/project';
import {
  AiAvatarValidatedDraft,
  CaptionsValidatedDraft,
  DubbingDraftParam,
  DubbingValidatedDraft,
  ReformattingDraftParam,
  ReformattingValidatedDraft,
  RephrasingValidatedDraft,
  ValidatedDraft,
} from '@contentlab/types/validatedDraft';
import {
  downloadAndParseSrt,
  srtStringToSrtItems,
  SrtItem,
} from '@contentlab/utils/srtUtils';
import {
  normalizeAvatarSpecConfig,
  parseAvatarSpecFromApi,
  getBaseConfigForDraft,
  parseScriptDataFromApi,
} from '@contentlab/views/contentlab/pages/ai-avatar-page/utils/aiAvatarDraftProjectUtils';
import queryClient from '@/utils/reactQueryClient';
import assetFetchers, {
  AssetFilters,
  AssetSorting,
} from '@/api/fetchers/content-studio/asset';
import {ASSETS, CONTENT_LAB} from '@/hooks/content-studio/consts';

// Mapper functions to convert Project to draft project types
const mapProjectToDraftDubbingProject = async (
  project: Project
): Promise<Partial<DraftDubbingProject>> => {
  const validatedDraft = project.validatedDraft as DubbingValidatedDraft;
  let selectedLanguages: string[] = [];
  let enableLipSync = true;

  if (validatedDraft) {
    const dubbingParams = validatedDraft.draft.params;
    if (dubbingParams.length > 0) {
      enableLipSync = dubbingParams[0].lipSync ?? true;
    }
    selectedLanguages = dubbingParams.map((param) => param.outputLanguage.name);
  }

  return {
    projectId: project.id,
    projectName: project.name,
    type: ProjectType.DUBBING,
    isInitialized: true,
    enableLipSync,
    selectedLanguages: [...new Set(selectedLanguages)],
  };
};

const mapProjectToDraftReformatProject = async (
  project: Project
): Promise<Partial<DraftReformatProject>> => {
  const validatedDraft = project.validatedDraft as ReformattingValidatedDraft;
  let selectedAspectRatios: AspectRatio[] = [];

  if (validatedDraft) {
    const reformatParams = validatedDraft.draft.params;
    selectedAspectRatios = reformatParams.map(
      (param) => param.outputAspectRatio as AspectRatio
    );
  }

  return {
    projectId: project.id,
    projectName: project.name,
    type: ProjectType.REFORMAT,
    isInitialized: true,
    selectedAspectRatios: [...new Set(selectedAspectRatios)],
  };
};

const mapProjectToDraftRephrasingProject = async (
  project: Project
): Promise<Partial<DraftRephrasingProject>> => {
  const validatedDraft = project.validatedDraft as RephrasingValidatedDraft;
  let assetId = '';
  let enableLipSync = true;
  let currentSrt: SrtItem[] = [];

  // Extract data from validatedDraft if available
  if (validatedDraft) {
    assetId = validatedDraft.draft.inputAsset;
    enableLipSync = validatedDraft.draft.enableLipSync;
    // only process if there were changes to the transcription
    if (validatedDraft.draft.outputTranscription) {
      currentSrt = srtStringToSrtItems(
        validatedDraft.draft.outputTranscription
      );
    }
  }
  if (validatedDraft?.draft.transcriptionUrl) {
    const mappedSrt = await downloadAndParseSrt(
      validatedDraft.draft.transcriptionUrl
    );
    // choose the srt source => if the outputTranscription is null then currentSrt would be an empty array
    const srtSource = currentSrt.length ? currentSrt : mappedSrt;
    currentSrt = srtSource;
  }

  return {
    projectId: project.id,
    projectName: project.name,
    type: ProjectType.REPHRASING,
    isInitialized: true,
    transcriptionLoaded: true,
    transcriptionUrl: validatedDraft.draft.transcriptionUrl,
    currentSrt,
    assetId,
    enableLipSync,
  };
};

const mapProjectToDraftAiAvatarProject = async (
  project: Project
): Promise<Partial<DraftAiAvatarProject>> => {
  const validatedDraft = project.validatedDraft as AiAvatarValidatedDraft;
  let assetId = '';

  if (validatedDraft && validatedDraft.draft) {
    assetId = validatedDraft.draft.assetId || '';
  }

  // Parse scriptData from the draft using shared utility
  const scriptDataFromApi = validatedDraft?.draft?.scriptData;
  const scriptData = parseScriptDataFromApi(scriptDataFromApi);

  // Parse avatarSpec from the draft using shared utility
  const avatarSpecFromApi = validatedDraft?.draft?.avatarSpec;
  const {
    selectedAvatarKind,
    libraryId,
    imageAssetId,
    voiceIdVoiceOnly,
    voiceIdCustom,
    avatarAudioReductionFactor,
    backgroundAudioReductionFactor,
    configLibrary,
    configCustom,
  } = parseAvatarSpecFromApi(avatarSpecFromApi);

  // Create deep copy for snapshot to ensure independence
  // Fallback to empty array if cloning fails or scripts is invalid
  let scriptsSnapshot: typeof scriptData.scripts;
  try {
    if (Array.isArray(scriptData.scripts)) {
      scriptsSnapshot = cloneDeep(scriptData.scripts);
    } else {
      scriptsSnapshot = [];
    }
  } catch (error) {
    console.error('Failed to clone scripts snapshot:', error);
    // Fallback to shallow copy or empty array if deep clone fails
    scriptsSnapshot = Array.isArray(scriptData.scripts)
      ? [...scriptData.scripts]
      : [];
  }

  return {
    projectId: project.id,
    projectName: project.name,
    type: ProjectType.AI_AVATAR,
    isInitialized: true,
    assetId,
    scriptData,
    scriptsSnapshot,
    avatarSpec: {
      voiceId: null, // Legacy field, use voiceIdVoiceOnly or voiceIdCustom instead
      voiceIdVoiceOnly,
      voiceIdCustom,
      libraryId,
      imageAssetId,
      imagePreviewUrl: null, // Will be loaded separately if needed
      avatarAudioReductionFactor,
      backgroundAudioReductionFactor,
      // Base config: use the appropriate config based on selectedAvatarKind, or default
      config: getBaseConfigForDraft(
        selectedAvatarKind,
        configLibrary,
        configCustom
      ),
      configLibrary: configLibrary ?? normalizeAvatarSpecConfig(undefined),
      configCustom: configCustom ?? normalizeAvatarSpecConfig(undefined),
    },
    selectedAvatarKind,
    activeEditorTab: AiAvatarEditorTab.SCRIPTS,
    configMode:
      selectedAvatarKind === AvatarKind.CUSTOM
        ? ConfigMode.CUSTOM
        : ConfigMode.LIBRARY,
    isAvatarLayoutOpen: false,
    isCustomizeScriptOpen: false,
    isAvatarPreviewMuted: true,
  };
};

const mapProjectToDraftCaptionsProject = async (
  project: Project
): Promise<Partial<DraftCaptionsProject>> => {
  const validatedDraft = project.validatedDraft as CaptionsValidatedDraft;
  let assetId = '';
  let currentSrt: SrtItem[] = [];
  let selectedStyle = null;
  let originalLanguage = 'English';
  let selectedLanguages: string[] = [];

  if (validatedDraft && 'assetId' in validatedDraft.draft) {
    assetId = validatedDraft.draft.assetId;
    selectedStyle = validatedDraft.draft.style;
    originalLanguage = validatedDraft.draft.originalLanguage;
    selectedLanguages = validatedDraft.draft.languages;
  }
  if (validatedDraft?.draft.transcriptionUrl) {
    const mappedSrt = await downloadAndParseSrt(
      validatedDraft.draft.transcriptionUrl
    );
    currentSrt = mappedSrt;
  }

  return {
    projectId: project.id,
    projectName: project.name,
    type: ProjectType.CAPTIONS,
    isInitialized: true,
    transcriptionLoaded: !!validatedDraft?.draft.transcriptionUrl,
    currentSrt,
    originalSrt: currentSrt,
    assetId,
    selectedStyle,
    selectedLanguages,
    originalLanguage,
    outputTranscriptionAssetId: null,
  };
};

export async function getMappedProjectByType(
  project: Project,
  type: ProjectType
) {
  switch (type) {
    case ProjectType.DUBBING:
      return mapProjectToDraftDubbingProject(project);
    case ProjectType.REFORMAT:
      return mapProjectToDraftReformatProject(project);
    case ProjectType.REPHRASING:
      return mapProjectToDraftRephrasingProject(project);
    case ProjectType.CAPTIONS:
      return mapProjectToDraftCaptionsProject(project);
    case ProjectType.AI_AVATAR:
      return mapProjectToDraftAiAvatarProject(project);
    default:
      throw new Error(`ProjectType "${type}" is not supported`);
  }
}

// Helper function to extract asset IDs from validatedDraft
export const extractAssetIdsFromValidatedDraft = (
  validatedDraft: ValidatedDraft,
  feature: FeatureName
): string[] => {
  if (!validatedDraft) return [];

  const assetIds: string[] = [];

  if (
    feature === FeatureName.REPHRASING &&
    'inputAsset' in validatedDraft.draft
  ) {
    assetIds.push(validatedDraft.draft.inputAsset);
  } else if (
    feature === FeatureName.CAPTIONS &&
    'assetId' in validatedDraft.draft
  ) {
    assetIds.push(validatedDraft.draft.assetId);
  } else if (
    feature === FeatureName.AI_AVATAR &&
    'assetId' in validatedDraft.draft
  ) {
    // For AI Avatar, extract assetId directly from draft
    const aiAvatarDraft = validatedDraft.draft as {assetId: string};
    if (aiAvatarDraft.assetId) {
      assetIds.push(aiAvatarDraft.assetId);
    }
  } else if ('params' in validatedDraft.draft) {
    if (feature === FeatureName.AI_DUBBING) {
      const params = validatedDraft.draft.params as DubbingDraftParam[];
      // For dubbing, extract input_assetId from params
      params.forEach((param) => {
        if (param.inputAssetId && !assetIds.includes(param.inputAssetId)) {
          assetIds.push(param.inputAssetId);
        }
      });
    } else if (feature === FeatureName.REFORMATTING) {
      const params = validatedDraft.draft.params as ReformattingDraftParam[];
      // For reformatting, extract assetId from params
      params.forEach((param) => {
        if (param.assetId && !assetIds.includes(param.assetId)) {
          assetIds.push(param.assetId);
        }
      });
    }
  }

  return assetIds;
};

// Helper function to fetch assets and convert to UploadedVideo format
export const fetchAndConvertAssetsToUploadedVideos = async (
  assetIds: string[],
  feature: FeatureName
): Promise<UploadedVideo[]> => {
  if (assetIds.length === 0) return [];

  try {
    const sorting: AssetSorting = {field: 'created_at', order: 'desc'};
    const filters: AssetFilters = {type: 'upload', ids: assetIds};
    const assetsResponse = await assetFetchers.getAssets({
      sorting,
      filters,
    });

    queryClient.setQueryData(
      [CONTENT_LAB, ASSETS, sorting, filters],
      assetsResponse
    );

    return assetsResponse.assets.map((asset) => ({
      id: asset.id,
      isUploading: false,
      thumbnail: asset.thumbnailUrl,
      fileName: asset.name,
      type: 'Uploads' as const,
      url: asset.url,
      durationMillis: asset.metadata?.durationMillis || 0,
      aspectRatio: asset.metadata?.aspectRatio as AspectRatio,
      feature,
    }));
  } catch (error) {
    console.error('Failed to fetch assets:', error);
    return [];
  }
};
